import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Fluid} from 'primeng/fluid';
import {FormBuilder, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {TranslocoPipe} from '@jsverse/transloco';
import {
  CustomerRegisterControllerService,
  RegisterProjectUserRequest,
  ScopedTokenControllerService,
  TokenScope,
} from '@frontend/shared-api';
import {Subscription} from 'rxjs';
import {FormValidationService} from '../../../../shared/services/form-validation.service';
import {FormFieldComponent} from '../../../../shared/components/inputs/form-field/form-field.component';
import {Button} from 'primeng/button';
import {Checkbox} from 'primeng/checkbox';
import {AuthService} from '../../../../shared/services/auth.service';
import {APP_ROUTES} from '../../../../app.routes';
import {environment} from '../../../../../environments/environment';

@Component({
  selector: 'app-project-user-registration',
  imports: [
    Fluid,
    FormsModule,
    ReactiveFormsModule,
    FormFieldComponent,
    TranslocoPipe,
    Button,
    Checkbox,
  ],
  templateUrl: './project-user-registration.component.html',
})
export class ProjectUserRegistrationComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly registerService = inject(CustomerRegisterControllerService);
  private readonly scopedTokenService = inject(ScopedTokenControllerService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  protected readonly authService = inject(AuthService);
  protected readonly newsletterEnabled = environment.newsletterEnabled;
  private readonly formValidation = inject(FormValidationService);

  submitting = false;
  loading = true;
  error = false;
  email = '';
  companyName = '';
  private invitationToken = '';

  private readonly langChangesSubscription: Subscription | undefined;

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      this.invitationToken = params['invitationToken'];
      if (this.invitationToken) {
        this.loadTokenDetails();
      } else {
        this.error = true;
        this.loading = false;
      }
    });
  }

  ngOnDestroy(): void {
    if (this.langChangesSubscription) {
      this.langChangesSubscription.unsubscribe();
    }
  }

  readonly form = this.fb.group(
    {
      email: [{value: '', disabled: true}, [Validators.required, Validators.email]],
      companyName: [{value: '', disabled: true}, Validators.required],
      password: [
        '',
        [Validators.required, Validators.minLength(8), this.formValidation.passwordValidator()],
      ],
      passwordRepeat: ['', Validators.required],
      newsletterSubscribed: [false],
    },
    {
      validators: this.formValidation.passwordMatchValidator(),
    },
  );

  private loadTokenDetails(): void {
    this.scopedTokenService
      .getTokenDetails({
        token: this.invitationToken,
        scope: TokenScope.PROJECT_INVITATION,
      })
      .subscribe({
        next: (tokenDetails) => {
          if (tokenDetails.isRegistered) {
            this.goToLogin();
            return;
          }
          this.email = tokenDetails.email;
          this.companyName = tokenDetails.originalTenantName ?? '';

          this.form.patchValue({
            email: this.email,
            companyName: this.companyName,
          });

          this.loading = false;
        },
        error: () => {
          this.error = true;
          this.loading = false;
        },
      });
  }

  onSubmit() {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach((key) => {
        const control = this.form.get(key);
        control?.markAllAsTouched();
      });
      return;
    }

    this.submitting = true;
    const formValues = this.form.value;

    if (!formValues.password) {
      this.submitting = false;
      return;
    }

    const registerRequest: RegisterProjectUserRequest = {
      token: this.invitationToken,
      email: this.email,
      password: formValues.password,
      newsletterSubscribed: formValues.newsletterSubscribed ?? false,
    };

    this.registerService
      .registerProjectInvitedUser({registerProjectUserRequest: registerRequest})
      .subscribe({
        next: () => {
          this.submitting = false;
          this.router.navigate([APP_ROUTES.REGISTRATION_SUCCESS]);
        },
        error: () => {
          this.submitting = false;
        },
      });
  }

  async goToLogin() {
    await this.authService.login(APP_ROUTES.PROJECT);
  }
}
